/**
 * NexusCapacityBoard - Clinician availability grid
 * Shows caseload, service lines, geography, accepting status
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Loader2, Users, MapPin, Briefcase, CheckCircle2, XCircle } from "lucide-react";

const NexusCapacityBoard = ({ token, API }) => {
  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceFilter, setServiceFilter] = useState("");
  const [countyFilter, setCountyFilter] = useState("");
  const [acceptingOnly, setAcceptingOnly] = useState(false);

  const fetchCapacity = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (serviceFilter) params.append("service_line", serviceFilter);
      if (countyFilter) params.append("county", countyFilter);
      if (acceptingOnly) params.append("accepting_only", "true");

      const res = await fetch(`${API}/api/nexus/capacity?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setClinicians(data.data || []);
    } catch (err) {
      toast.error("Failed to load capacity data");
    } finally {
      setLoading(false);
    }
  }, [API, token, serviceFilter, countyFilter, acceptingOnly]);

  useEffect(() => { fetchCapacity(); }, [fetchCapacity]);

  const getUtilColor = (pct) => {
    if (pct >= 90) return "bg-red-500";
    if (pct >= 70) return "bg-amber-500";
    if (pct >= 50) return "bg-yellow-400";
    return "bg-emerald-500";
  };

  return (
    <div data-testid="nexus-capacity-board" className="space-y-4">
      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="py-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="w-40">
              <Select value={serviceFilter} onValueChange={(v) => setServiceFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="capacity-service-filter"><SelectValue placeholder="Service Line" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  <SelectItem value="BA">BA</SelectItem>
                  <SelectItem value="IIC">IIC</SelectItem>
                  <SelectItem value="BPS">BPS</SelectItem>
                  <SelectItem value="FCT">FCT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Input
                data-testid="capacity-county-filter"
                placeholder="County..."
                value={countyFilter}
                onChange={(e) => setCountyFilter(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptingOnly}
                onChange={(e) => setAcceptingOnly(e.target.checked)}
                className="rounded border-slate-300"
              />
              Accepting only
            </label>
            <Button variant="outline" size="sm" onClick={fetchCapacity}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : clinicians.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">
            <Users className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No clinicians with Nexus profiles</p>
            <p className="text-sm mt-1">Set up clinician profiles in the Provider Directory tab</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {clinicians.map((c) => {
            const profile = c.nexus_profile || {};
            const util = c.caseload?.utilization_pct || 0;

            return (
              <Card key={c.clinician_id} data-testid={`capacity-card-${c.clinician_id}`} className="border-slate-200 hover:shadow-sm transition-shadow">
                <CardContent className="py-4 px-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-sm text-slate-900">{c.full_name}</p>
                      <p className="text-xs text-slate-500">{c.role}</p>
                    </div>
                    {c.accepting_new ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                        <CheckCircle2 className="w-3 h-3 mr-0.5" /> Accepting
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">
                        <XCircle className="w-3 h-3 mr-0.5" /> Closed
                      </Badge>
                    )}
                  </div>

                  {/* Utilization Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">Caseload</span>
                      <span className="font-medium">{c.caseload?.current || 0} / {c.caseload?.max || 25}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${getUtilColor(util)}`} style={{ width: `${Math.min(util, 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                      <span>{util}% utilized</span>
                      {c.caseload?.waitlist > 0 && <span>{c.caseload.waitlist} on waitlist</span>}
                    </div>
                  </div>

                  {/* Service Lines */}
                  {profile.service_lines && profile.service_lines.length > 0 && (
                    <div className="mb-2">
                      <div className="flex items-center gap-1 mb-1">
                        <Briefcase className="w-3 h-3 text-slate-400" />
                        <span className="text-[10px] text-slate-500">Services</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {profile.service_lines.map(s => (
                          <Badge key={s} variant="outline" className="text-[10px] px-1.5 py-0">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Geography */}
                  {profile.service_counties && profile.service_counties.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 mb-1">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        <span className="text-[10px] text-slate-500">Counties</span>
                      </div>
                      <p className="text-[10px] text-slate-600">{profile.service_counties.join(", ")}</p>
                    </div>
                  )}

                  {/* Languages */}
                  {profile.languages && profile.languages.length > 0 && (
                    <p className="text-[10px] text-slate-500 mt-1">
                      Languages: {profile.languages.join(", ")}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NexusCapacityBoard;
